import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faCheck, faLock } from "@fortawesome/free-solid-svg-icons";
import DynamicLayout from "../../../components/DynamicLayout";
import ProgressBar from "../../components/ProgressBar";
import { AcademyContext } from "../../../context/academyContext";
import saturnwallpaper from "../../../img/saturnwallpaper.jpg";
import { useNavigate } from "react-router-dom";

const AstrologyAcademy = () => {
  const { progress } = useContext(AcademyContext);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [selectedMode, setSelectedMode] = useState("beginner"); // "beginner" selected by default
  const navigate = useNavigate();

  const totalLessons = 47; // Total number of lessons in the astrology course (matches the lesson file)

  const toggleChapter = (chapter) => {
    setActiveChapter(activeChapter === chapter ? null : chapter);
  };

  const toggleLesson = (lesson) => {
    setActiveLesson(activeLesson === lesson ? null : lesson);
  };

  const renderIcon = (chapter, isAvailable) => {
    if (isAvailable) {
      return <FontAwesomeIcon icon={faCheck} className="has-text-warning ml-2" />;
    }
    return <FontAwesomeIcon icon={activeChapter === chapter ? faChevronDown : faChevronRight} className="ml-2" />;
  };

  const renderLessonIcon = (lesson, isAvailable) => {
    if (isAvailable) {
      return <FontAwesomeIcon icon={faCheck} className="has-text-warning ml-2" />;
    }
    return <FontAwesomeIcon icon={activeLesson === lesson ? faChevronDown : faChevronRight} className="ml-2" />;
  };

  const renderCheckMark = (lessonNumber) => {
    if (progress.astrology?.completedLessons >= lessonNumber) {
      return <FontAwesomeIcon icon={faCheck} className="has-text-warning ml-2" />;
    }
    return null;
  };

  const handleResumeClick = () => {
    const nextLesson = (progress.astrology?.completedLessons || 0) + 1;
    navigate(`/academy/astrology/${selectedMode}/lesson?lesson=${nextLesson}`);
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
  };

  const handleLessonClick = (lessonNumber) => {
    navigate(`/academy/astrology/${selectedMode}/lesson?lesson=${lessonNumber}`);
  };

  const renderLesson = (lessonNumber, lessonTitle, roundNumber) => {
    const isCompleted = progress.astrology?.completedLessons >= lessonNumber;
    const isAccessible = progress.astrology?.completedLessons >= lessonNumber - 1;
    
    return (
      <React.Fragment>
        <li
          className={`${isCompleted ? "has-text-warning" : isAccessible ? "has-text-grey" : "has-text-grey-light"} `}
          onClick={() => isAccessible && handleLessonClick(lessonNumber)}
          style={{ cursor: isAccessible ? 'pointer' : 'not-allowed' }}
        >
          {lessonTitle} {renderCheckMark(lessonNumber)}
          {!isAccessible && <FontAwesomeIcon icon={faLock} className="has-text-grey-light ml-2" />}
        </li>
      </React.Fragment>
    );
  };

  const hasStartedCourse = progress.astrology?.completedLessons > 0;
  const isHardModeUnlocked = true; // Temporarily enable Intermediate mode for development

  return (
    <DynamicLayout>
      <div
        className="full-screen-image-container heightauto margin-top-0 compact-course-hero"
        style={{
          backgroundImage: `url(${saturnwallpaper})`,
        }}
      >
        <section className="section hasbackgroundgrey heightauto has-text-centered" style={{ padding: '2.5rem 1.5rem 2.5rem' }}>
          <h2 className="title has-text-centered has-text-warning">
            {hasStartedCourse ? "Welcome back to the Astrology Course." : "Welcome to the Astrology Course."}
          </h2>
          <ProgressBar completedLessons={progress.astrology?.completedLessons || 0} totalLessons={totalLessons} />
          
          <ul>
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.astrology?.completedLessons >= 1 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter1")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 1: The Zodiac Wheel {renderIcon("chapter1", progress.astrology?.completedLessons >= 1)}
              </h2>
              <ul className={activeChapter === "chapter1" ? "" : "is-hidden"}>
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter1-foreword')}
                >
                  Foreword
                </li>
                {renderLesson(1, "Lesson 1: Structure and Layout of the Zodiac Wheel", 1)}
                {renderLesson(2, "Lesson 2: Understanding the Ecliptic and Celestial Sphere", 2)}
                {renderLesson(3, "Lesson 3: House Systems: Placidus, Whole Sign, Koch, and Other Systems", 3)}
                {renderLesson(4, "Lesson 4: Sidereal vs. Tropical Astrology", 4)}
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter1-flashcards')}
                >
                  Flashcard Round 1
                </li>
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.astrology?.completedLessons >= 7 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter2")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 2: The Zodiac Signs {renderIcon("chapter2", progress.astrology?.completedLessons >= 7)}
              </h2>
              <ul className={activeChapter === "chapter2" ? "" : "is-hidden"}>
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter2-foreword')}
                >
                  Foreword
                </li>
                {renderLesson(7, "Lesson 1: Aries", 1)}
                {renderLesson(8, "Lesson 2: Taurus", 2)}
                {renderLesson(9, "Lesson 3: Gemini", 3)}
                {renderLesson(10, "Lesson 4: Cancer", 4)}
                {renderLesson(11, "Lesson 5: Leo", 5)}
                {renderLesson(12, "Lesson 6: Virgo", 6)}
                {renderLesson(13, "Lesson 7: Libra", 7)}
                {renderLesson(14, "Lesson 8: Scorpio", 8)}
                {renderLesson(15, "Lesson 9: Sagittarius", 9)}
                {renderLesson(16, "Lesson 10: Capricorn", 10)}
                {renderLesson(17, "Lesson 11: Aquarius", 11)}
                {renderLesson(18, "Lesson 12: Pisces", 12)}
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter2-flashcards')}
                >
                  Flashcard Round 1
                </li>
              </ul>
            </li>
            
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.astrology?.completedLessons >= 19 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter3")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 3: The Planets {renderIcon("chapter3", progress.astrology?.completedLessons >= 19)}
              </h2>
              <ul className={activeChapter === "chapter3" ? "" : "is-hidden"}>
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter3-foreword')}
                >
                  Foreword
                </li>
                {renderLesson(19, "Lesson 1: Sun", 1)}
                {renderLesson(20, "Lesson 2: Moon", 2)}
                {renderLesson(21, "Lesson 3: Mercury", 3)}
                {renderLesson(22, "Lesson 4: Venus", 4)}
                {renderLesson(23, "Lesson 5: Mars", 5)}
                {renderLesson(24, "Lesson 6: Jupiter", 6)}
                {renderLesson(25, "Lesson 7: Saturn", 7)}
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter3-flashcards')}
                >
                  Flashcard Round 1
                </li>
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.astrology?.completedLessons >= 26 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter4")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 4: The Houses {renderIcon("chapter4", progress.astrology?.completedLessons >= 26)}
              </h2>
              <ul className={activeChapter === "chapter4" ? "" : "is-hidden"}>
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter4-foreword')}
                >
                  Foreword
                </li>
                {renderLesson(26, "Lesson 1: 1st House", 1)}
                {renderLesson(27, "Lesson 2: 2nd House", 2)}
                {renderLesson(28, "Lesson 3: 3rd House", 3)}
                {renderLesson(29, "Lesson 4: 4th House", 4)}
                {renderLesson(30, "Lesson 5: 5th House", 5)}
                {renderLesson(31, "Lesson 6: 6th House", 6)}
                {renderLesson(32, "Lesson 7: 7th House", 7)}
                {renderLesson(33, "Lesson 8: 8th House", 8)}
                {renderLesson(34, "Lesson 9: 9th House", 9)}
                {renderLesson(35, "Lesson 10: 10th House", 10)}
                {renderLesson(36, "Lesson 11: 11th House", 11)}
                {renderLesson(37, "Lesson 12: 12th House", 12)}
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter4-flashcards')}
                >
                  Flashcard Round 1
                </li>
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.astrology?.completedLessons >= 5 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter5")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 5: Astrological Symbols {renderIcon("chapter5", progress.astrology?.completedLessons >= 5)}
              </h2>
              <ul className={activeChapter === "chapter5" ? "" : "is-hidden"}>
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter5-foreword')}
                >
                  Foreword
                </li>
                {renderLesson(5, "Lesson 1: Sign Symbols", 1)}
                {renderLesson(6, "Lesson 2: Planets Symbols", 2)}
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter5-flashcards')}
                >
                  Flashcard Round 1
                </li>
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.astrology?.completedLessons >= 38 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter6")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 6: Aspects and Transits {renderIcon("chapter6", progress.astrology?.completedLessons >= 38)}
              </h2>
              <ul className={activeChapter === "chapter6" ? "" : "is-hidden"}>
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter6-foreword')}
                >
                  Foreword
                </li>
                {renderLesson(38, "Lesson 1: Understanding Aspects", 1)}
                {renderLesson(39, "Lesson 2: Conjunction", 2)}
                {renderLesson(40, "Lesson 3: Opposition", 3)}
                {renderLesson(41, "Lesson 4: Trine", 4)}
                {renderLesson(42, "Lesson 5: Square", 5)}
                {renderLesson(43, "Lesson 6: Sextile", 6)}
                {renderLesson(44, "Lesson 7: Aspect Orbs", 7)}
                {renderLesson(45, "Lesson 8: Aspect Patterns", 8)}
                {renderLesson(46, "Lesson 9: Planetary Dignities", 9)}
                {renderLesson(47, "Lesson 10: Interpreting Aspects", 10)}
                <li
                  className={`is-size-6 ${progress.astrology?.completedLessons >= 0 ? 'has-text-grey-light is-clickable' : 'has-text-grey-light' } ml-4 mb-2`}
                  style={{ cursor: progress.astrology?.completedLessons >= 0 ? 'pointer' : 'not-allowed' }}
                  onClick={() => progress.astrology?.completedLessons >= 0 && handleLessonClick('chapter6-flashcards')}
                >
                  Flashcard Round 1
                </li>
              </ul>
            </li>
          </ul>
          <button className="button is-warning mt-6" onClick={handleResumeClick}>
            {hasStartedCourse ? "Resume" : "Start"}
          </button>

          <div className="mt-5 buttons has-addons is-centered" style={{ marginBottom: '2rem' }}>
            <button
              className={`button is-small ${selectedMode === "beginner" ? "is-warning" : ""}`}
              onClick={() => handleModeChange("beginner")}
            >
              Beginner
            </button>
            <button
              className={`button is-small ${selectedMode === "intermediate" ? "is-warning" : ""} ${isHardModeUnlocked ? '' : 'is-locked'}`}
              onClick={() => handleModeChange("intermediate")}
              disabled={!isHardModeUnlocked}
              title={isHardModeUnlocked ? '' : 'Complete all lessons to unlock Intermediate mode'}
            >
              {!isHardModeUnlocked && <FontAwesomeIcon icon={faLock} className="lock-icon" />}
              Intermediate
            </button>
          </div>
        </section>
      </div>
    </DynamicLayout>
  );
};

export default AstrologyAcademy;
